<script>
  /**
   * Compact operator index of Mini App clients.
   * Full live-parity panel lives on /dashboard/client/:telegram_id.
   */
  import { onMount } from 'svelte'
  import { getClientAdmin } from '../../api.js'
  import { link } from '../../lib/historyNav.js'
  import { OPS_ERROR_RU, fmtNum } from '../../lib/format.js'

  let payload = null
  let err = ''
  let busy = false

  $: accounts = (payload && payload.accounts) || []
  $: deposit = (payload && payload.deposit) || null

  function errRu(code, fallback) {
    if (!code) return fallback || 'ошибка'
    return OPS_ERROR_RU[code] || fallback || code
  }

  function modeRu(acc) {
    if (acc.imitation) return 'имитация'
    const m = String(acc.trading_mode || '')
    if (m === 'live') return 'вкл'
    if (m === 'disabled') return 'выкл'
    return m || '—'
  }

  function equityTxt(acc) {
    if (acc == null || acc.equity == null) return '—'
    const n = Number(acc.equity)
    if (!Number.isFinite(n)) return '—'
    return fmtNum(n)
  }

  async function load() {
    err = ''
    busy = true
    try {
      const { res, data, unauthorized } = await getClientAdmin()
      if (unauthorized) return
      if (!res?.ok || !data?.ok) {
        err = errRu(data && data.error, 'Не удалось загрузить клиентов')
        return
      }
      payload = data
    } catch {
      err = 'Сеть / API недоступны'
    } finally {
      busy = false
    }
  }

  onMount(() => {
    load()
  })
</script>

<div class="client-admin">
  <section class="pm-surface" aria-label="Клиенты">
    <div class="pm-section-head">
      <h2>Клиенты Mini App</h2>
      <button type="button" class="pm-btn" disabled={busy} on:click={load}>Обновить</button>
    </div>
    <p class="pm-meta">
      Список без полных панелей. Откройте клиента, чтобы видеть его live: стратегии, баланс, ставки.
    </p>
    {#if err}
      <p class="pm-error">{err}</p>
    {/if}

    {#if !payload}
      <p class="pm-empty">Загрузка…</p>
    {:else if !accounts.length}
      <p class="pm-empty">Нет записей в accounts.json</p>
    {:else}
      <div class="client-index-wrap">
        <table class="client-index">
          <thead>
            <tr>
              <th scope="col">Имя</th>
              <th scope="col">id</th>
              <th scope="col">Режим</th>
              <th scope="col">Белый список</th>
              <th scope="col" class="num">Equity</th>
            </tr>
          </thead>
          <tbody>
            {#each accounts as acc (acc.telegram_id)}
              <tr class:is-imitation={acc.imitation} class:is-revoked={acc.revoked}>
                <td>
                  <a
                    class="client-index__name"
                    href={`/dashboard/client/${acc.telegram_id}`}
                    use:link
                  >
                    {acc.display_name || acc.telegram_id}
                  </a>
                  {#if acc.imitation}
                    <span class="client-index__hint">имитация, без CLOB</span>
                  {:else if acc.risk_note_ru}
                    <span class="client-index__hint">{acc.risk_note_ru}</span>
                  {/if}
                </td>
                <td class="mono">{acc.telegram_id}</td>
                <td>{modeRu(acc)}</td>
                <td>{acc.waitlisted ? 'да' : 'отозван'}</td>
                <td class="num mono">{equityTxt(acc)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </section>

  {#if deposit}
    <section class="pm-surface" aria-label="Депозит и вывод">
      <div class="pm-section-head">
        <h2>{deposit.title}</h2>
      </div>
      {#each deposit.blocks || [] as b (b.title)}
        <div class="client-admin__block">
          <h3>{b.title}</h3>
          <p>{b.body}</p>
        </div>
      {/each}
    </section>
  {/if}
</div>

<style>
  .client-admin {
    display: grid;
    gap: 1rem;
  }
  .client-index-wrap {
    overflow-x: auto;
  }
  .client-index {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.88rem;
  }
  .client-index th {
    text-align: left;
    font-size: 0.62rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
    padding: 0.25rem 0.65rem 0.45rem 0;
    border-bottom: 1px solid var(--pm-line);
  }
  .client-index th.num,
  .client-index td.num {
    text-align: right;
  }
  .client-index td {
    padding: 0.55rem 0.65rem 0.55rem 0;
    border-bottom: 1px solid var(--pm-line);
    vertical-align: baseline;
  }
  .client-index tr.is-imitation td {
    opacity: 0.88;
  }
  .client-index tr.is-revoked td {
    opacity: 0.7;
  }
  .client-index__name {
    font-weight: 600;
    color: var(--pm-ink);
    text-decoration: none;
    letter-spacing: -0.02em;
  }
  .client-index__name:hover {
    text-decoration: underline;
  }
  .client-index__hint {
    display: block;
    margin-top: 0.15rem;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
  }
  .client-admin__block {
    margin-top: 0.75rem;
  }
  .client-admin__block h3 {
    margin: 0 0 0.35rem;
    font-size: 0.88rem;
  }
  .client-admin__block p {
    margin: 0;
    font-size: 0.88rem;
    line-height: 1.45;
    color: var(--pm-ink-soft);
  }
  .mono {
    font-variant-numeric: tabular-nums;
  }
</style>
